import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Phone,
  Mail,
  MapPin,
  FileText,
  TrendingUp,
  Users,
  DollarSign,
  ShieldAlert,
  Bot,
  Pencil,
  Target,
  ArrowRight,
  Zap,
  Flag,
} from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Skeleton,
} from "@/components/ui";
import { useFetchSettingsTenantQuery } from "@/hooks";
import { useDashboard } from "@/hooks/queries/dashboard/useDashboard";

export function BusinessOverview() {
  const navigate = useNavigate();
  const { data: settings, isLoading: settingsLoading } = useFetchSettingsTenantQuery();
  const { metrics, metricsLoading } = useDashboard();

  const totalChurn = metrics?.churnRisk
    ? metrics.churnRisk.low + metrics.churnRisk.medium + metrics.churnRisk.high
    : 0;
  const retentionRate = totalChurn > 0 && metrics?.churnRisk
    ? Math.round(((metrics.churnRisk.low) / totalChurn) * 100)
    : 0;
  const avgTicket = metrics?.appointmentsCount
    ? metrics.revenue / metrics.appointmentsCount
    : 0;

  return (
    <div className="space-y-8">
      {/* Business Identity Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-sm border border-app-border-subtle overflow-hidden"
      >
        <div className="bg-brand h-24 relative">
          <div className="absolute -bottom-10 left-8">
            <Avatar className="w-20 h-20 border-4 border-white shadow-lg rounded-2xl">
              <AvatarImage src="" />
              <AvatarFallback className="rounded-2xl bg-white text-brand">
                <Building2 className="w-8 h-8" />
              </AvatarFallback>
            </Avatar>
          </div>
          {/* C-08: the clinic record lives at /settings/clinic. */}
          <button
            aria-label="Editar perfil da clínica"
            onClick={() => navigate("/settings/clinic")}
            className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 rounded-xl transition-all"
          >
            <Pencil className="w-4 h-4" />
          </button>
        </div>

        <div className="pt-14 pb-6 px-8">
          {settingsLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-7 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-app-fg">
                {settings?.name || "Sua Clínica"}
              </h2>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-sm text-app-fg-muted">
                {settings?.cpf && (
                  <span className="flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5" />
                    {settings.cpf}
                  </span>
                )}
                {settings?.phone && (
                  <span className="flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5" />
                    {settings.phone}
                  </span>
                )}
                {settings?.email && (
                  <span className="flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" />
                    {settings.email}
                  </span>
                )}
                {settings?.address && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" />
                    {settings.address}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </motion.div>

      {/* Strategic KPIs */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1.5 h-6 bg-brand rounded-full" />
          <h3 className="text-lg font-bold text-app-fg">Indicadores Estratégicos</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StrategicKPI
            label="Taxa de Retenção"
            value={metricsLoading ? null : `${retentionRate}%`}
            icon={Users}
            color="emerald"
            description="Pacientes que retornam"
          />
          <StrategicKPI
            label="Ticket Médio"
            value={metricsLoading ? null : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(avgTicket)}
            icon={DollarSign}
            color="blue"
            description="Receita por atendimento"
          />
          <StrategicKPI
            label="Pacientes em Risco"
            value={metricsLoading ? null : `${metrics?.churnRisk?.high || 0}`}
            icon={ShieldAlert}
            color="red"
            description="Alto risco de evasão"
          />
          <StrategicKPI
            label="Interações IA"
            value={metricsLoading ? null : `${metrics?.aiEngagements || 0}`}
            icon={Bot}
            color="indigo"
            description="Atendimentos automatizados"
          />
        </div>
      </motion.div>

      {/* SWOT Summary + Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
      >
        {/* Last SWOT Summary */}
        <div className="bg-white rounded-2xl shadow-sm border border-app-border-subtle p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-app-fg flex items-center gap-2">
              <Target className="w-5 h-5 text-brand" />
              Última Análise SWOT
            </h3>
          </div>

          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="bg-brand-soft p-4 rounded-2xl mb-4">
              <Bot className="w-10 h-10 text-brand" />
            </div>
            <p className="text-app-fg-muted mb-4 max-w-sm">
              Inicie uma análise SWOT com o Dr. Insights para obter recomendações estratégicas personalizadas.
            </p>
            <button
              onClick={() => navigate("/my-business/strategy")}
              className="flex items-center gap-2 px-5 py-2.5 bg-brand text-white rounded-xl font-medium shadow-md hover:shadow-lg hover:scale-105 transition-all text-sm"
            >
              <Zap className="w-4 h-4" />
              Iniciar Análise SWOT
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl shadow-sm border border-app-border-subtle p-6">
          <h3 className="text-lg font-bold text-app-fg flex items-center gap-2 mb-4">
            <Flag className="w-5 h-5 text-brand" />
            Ações Rápidas
          </h3>
          <div className="space-y-3">
            <QuickAction
              title="Editar Perfil da Clínica"
              description="Atualize nome, CNPJ, logo e dados de contato"
              onClick={() => navigate("/settings/clinic")}
              icon={Building2}
              color="teal"
            />
            <QuickAction
              title="Análise Estratégica SWOT"
              description="O Dr. Insights cruza suas respostas com dados da clínica"
              onClick={() => navigate("/my-business/strategy")}
              icon={Target}
              color="emerald"
            />
            {/* SCRUM-208 (audit C-17): "Vend+" is a retired internal name and
                `/growth` only exists as a redirect. One name, one route. */}
            <QuickAction
              title="Ver equipe de IA"
              description="Quem atende, o que cada agente pode fazer e o que fizeram hoje"
              onClick={() => navigate("/growth-team")}
              icon={TrendingUp}
              color="blue"
            />
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function StrategicKPI({
  label,
  value,
  icon: Icon,
  color,
  description,
}: {
  label: string;
  value: string | null;
  icon: any;
  color: string;
  description: string;
}) {
  const colorMap: Record<string, { bg: string; text: string; iconBg: string }> = {
    emerald: { bg: "bg-app-success/10", text: "text-app-success", iconBg: "bg-app-success/10 text-app-success" },
    blue: { bg: "bg-app-info/10", text: "text-app-info", iconBg: "bg-app-info/10 text-app-info" },
    red: { bg: "bg-app-danger/10", text: "text-app-danger", iconBg: "bg-app-danger/10 text-app-danger" },
    indigo: { bg: "bg-brand-soft", text: "text-brand-hover", iconBg: "bg-brand-soft text-brand" },
  };

  const c = colorMap[color] || colorMap.emerald;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-app-border-subtle p-5 flex flex-col justify-between h-36">
      <div className="flex justify-between items-start">
        <p className="text-sm font-medium text-app-fg-muted">{label}</p>
        <div className={`p-2 rounded-lg ${c.iconBg}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div>
        {value === null ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <h3 className="text-2xl font-bold text-app-fg">{value}</h3>
        )}
        <p className="text-xs text-app-fg-subtle mt-1">{description}</p>
      </div>
    </div>
  );
}

function QuickAction({
  title,
  description,
  onClick,
  icon: Icon,
  color,
  disabled,
}: {
  title: string;
  description: string;
  onClick: () => void;
  icon: any;
  color: string;
  disabled?: boolean;
}) {
  const colorMap: Record<string, string> = {
    teal: "bg-brand-soft text-brand",
    emerald: "bg-app-success/10 text-app-success",
    blue: "bg-app-info/10 text-app-info",
    zinc: "bg-app-surface-muted text-app-fg-subtle",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-4 p-4 rounded-xl border border-app-border-subtle text-left transition-all ${
 disabled
          ? "opacity-50 cursor-not-allowed"
          : "hover:bg-app-surface-muted hover:border-app-border hover:shadow-sm cursor-pointer"
      }`}
    >
      <div className={`p-2.5 rounded-xl ${colorMap[color] || colorMap.teal}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-app-fg text-sm">{title}</p>
        <p className="text-xs text-app-fg-muted mt-0.5">{description}</p>
      </div>
      {!disabled && <ArrowRight className="w-4 h-4 text-app-fg-subtle shrink-0" />}
    </button>
  );
}
